'use client'

import React from 'react'
import { PartnerSliderBlock } from '@/payload-types'
import Image from 'next/image'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { Header } from '@/ui/components/Header'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { localizedHref } from '@/utils/localizedHref'

export const PartnerSliderBlockComponent: React.FC<PartnerSliderBlock & { hideCta?: boolean }> = ({
  label,
  title,
  description,
  partners,
  // /become-a-partner reuses the marquee but has its own request form below it,
  // so the generic "Join these Companies → /login" button would mislead there.
  hideCta = false,
}) => {
  const locale = useLocale()
  if (!partners || partners.length === 0) return null

  // Split the logos into two rows that scroll together in the same direction.
  const midpoint = Math.ceil(partners.length / 2)
  const rows = [partners.slice(0, midpoint), partners.slice(midpoint)].filter(
    (row) => row.length > 0,
  )

  const renderPartner = (partner: (typeof partners)[number], index: number) => {
    if (!partner) return null

    const logo = typeof partner.logo === 'object' ? partner.logo : null
    const link = partner.link

    const href =
      link?.type === 'reference' && typeof link.reference?.value === 'object' && link.reference.value.path
        ? localizedHref(link.reference.value.path, locale)
        : localizedHref(link?.url, locale)

    const target = link?.newTab ? '_blank' : undefined
    const rel = link?.newTab ? 'noopener noreferrer' : undefined

    return (
      <a
        key={`${index}-${partner.id || index}`}
        href={href}
        target={target}
        rel={rel}
        className="transition-opacity duration-300 hover:opacity-80 flex-shrink-0 min-w-[180px]"
      >
        {logo && logo.url && (
          <div className="flex items-center justify-center w-[162px] h-[162px]">
            <Image
              src={logo.url}
              alt={logo.alt || 'Partner logo'}
              width={logo.width || 150}
              height={logo.height || 60}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </a>
    )
  }

  return (
    <div className="w-full">
      {/* Header Section - Left Aligned */}
      <Header label={label} title={title} description={description} />

      {/* Slider Section - Breaks out to full viewport width */}
      <div className="relative mb-12 lg:mb-16 w-screen -ml-[50vw] left-[50%] overflow-hidden">
        {/* Partners Slider Container with Gradients */}
        <div className="relative py-8 lg:py-12 w-full">
          {/* Two rows, both sliding in the same direction at the same speed */}
          <div className="overflow-hidden w-full flex flex-col gap-6 lg:gap-8">
            <style jsx>{`
              @keyframes slide {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-33.333%);
                }
              }
              .animate-slide {
                animation: slide var(--slide-duration) linear infinite;
                will-change: transform;
              }
            `}</style>
            {rows.map((row, rowIndex) => {
              // Triple the row for a truly seamless infinite scroll.
              const duplicatedRow = [...row, ...row, ...row]
              return (
                <div
                  key={rowIndex}
                  className="flex items-center gap-6 lg:gap-8 animate-slide w-max"
                  style={{ '--slide-duration': `${row.length * 4}s` } as React.CSSProperties}
                >
                  {duplicatedRow.map((partner, index) => renderPartner(partner, index))}
                </div>
              )
            })}
          </div>

          {/* Left Gradient — narrow on phones: two w-64 fades on a 390px screen covered the
              whole strip and the logo marquee read as an empty section (2026-07-24 audit) */}
          <div className="absolute left-0 top-0 bottom-0 w-10 sm:w-32 md:w-64 lg:w-80 xl:w-[400px] 2xl:w-[500px] bg-gradient-to-r from-white from-10% via-white via-40% to-transparent z-20 pointer-events-none" />

          {/* Right Gradient */}
          <div className="absolute right-0 top-0 bottom-0 w-10 sm:w-32 md:w-64 lg:w-80 xl:w-[400px] 2xl:w-[500px] bg-gradient-to-l from-white from-10% via-white via-40% to-transparent z-20 pointer-events-none" />
        </div>
      </div>

      {/* Button Section */}
      {!hideCta && (
      <div className="flex justify-center mb-12">
        <Button asChild variant="primary" size="md">
          <Link href="/login">
            <ButtonContent label="Join these Companies" />
          </Link>
        </Button>
      </div>
      )}
    </div>
  )
}
